export function MarketOpportunities() {
  const opportunities = [
    {
      area: 'Surrey Central & City Centre',
      demand: 'High',
      avgNightly: '$120 - $180',
      occupancy: '65-75%',
      highlights: [
        'Major business district with corporate travel demand',
        'SkyTrain access to Vancouver and airport',
        'Growing entertainment and dining scene',
        'New condo developments with STR potential'
      ]
    },
    {
      area: 'Guildford Town Centre',
      demand: 'Medium-High',
      avgNightly: '$100 - $150',
      occupancy: '60-70%',
      highlights: [
        'Major shopping and commercial hub',
        'Family-friendly neighborhood appeal',
        'Good highway access for visitors',
        'Lower competition than Vancouver'
      ]
    },
    {
      area: 'Cloverdale & South Surrey',
      demand: 'Medium',
      avgNightly: '$110 - $160',
      occupancy: '55-65%',
      highlights: [
        'Historic charm and community events',
        'Proximity to US border for cross-border travelers',
        'Equestrian and agricultural tourism',
        'Quieter suburban setting with parking'
      ]
    },
    {
      area: 'Newton & Whalley',
      demand: 'Medium',
      avgNightly: '$90 - $130',
      occupancy: '50-60%',
      highlights: [
        'Most affordable entry point for investors',
        'Growing immigrant communities with visiting family demand',
        'Transit improvements underway',
        'High rental yield potential'
      ]
    }
  ];

  const investmentFactors = [
    {
      icon: 'ri-funds-line',
      title: 'Lower Entry Costs',
      description: 'Surrey property prices 30-40% lower than Vancouver, allowing better cash flow and ROI for STR investments.'
    },
    {
      icon: 'ri-arrow-up-line',
      title: 'Growth Trajectory',
      description: 'Rapid population growth and urban development creating increasing demand for short-term accommodations.'
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Strategic Location',
      description: 'Central position in Metro Vancouver with easy access to airport, Vancouver, and US border for diverse guest base.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Early Mover Advantage',
      description: 'Establish compliant operations now before market becomes saturated as regulations formalize.'
    }
  ];

  return (
    <section className="py-24 lg:py-32 bg-[#F0F0F0]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2 className="font-['Fraunces'] text-4xl lg:text-5xl font-bold text-[#505A5A] mb-6">
            Market Opportunities in Surrey
          </h2>
          <p className="text-lg text-[#505A5A]/70 leading-relaxed">
            Surrey's diverse neighborhoods offer varied opportunities for short-term rental success. Understanding local market dynamics is key to maximizing returns.
          </p>
        </div>

        {/* Neighborhood Analysis */}
        <div className="mb-20">
          <h3 className="text-2xl font-semibold text-[#505A5A] mb-10 text-center">
            Neighborhood Market Analysis
          </h3>
          <div className="grid lg:grid-cols-2 gap-8">
            {opportunities.map((area, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-all"
              >
                <div className="flex items-start justify-between mb-6">
                  <h4 className="text-xl font-semibold text-[#505A5A]">{area.area}</h4>
                  <span className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${
                    area.demand === 'High' ? 'bg-[#882629] text-white' :
                    area.demand === 'Medium-High' ? 'bg-[#882629]/80 text-white' :
                    'bg-[#882629]/60 text-white'
                  }`}>
                    {area.demand} Demand
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-[#F0F0F0] rounded-xl p-4">
                    <div className="text-sm text-[#505A5A]/70 mb-1">Avg. Nightly Rate</div>
                    <div className="text-lg font-semibold text-[#882629]">{area.avgNightly}</div>
                  </div>
                  <div className="bg-[#F0F0F0] rounded-xl p-4">
                    <div className="text-sm text-[#505A5A]/70 mb-1">Occupancy Rate</div>
                    <div className="text-lg font-semibold text-[#882629]">{area.occupancy}</div>
                  </div>
                </div>

                <ul className="space-y-3">
                  {area.highlights.map((highlight, idx) => (
                    <li key={idx} className="flex items-start">
                      <i className="ri-star-fill text-[#882629] text-sm mr-3 flex-shrink-0 mt-1"></i>
                      <span className="text-[#505A5A]/80 text-sm leading-relaxed">{highlight}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Investment Factors */}
        <div>
          <h3 className="text-2xl font-semibold text-[#505A5A] mb-10 text-center">
            Why Invest in Surrey STRs Now
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {investmentFactors.map((factor, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-6 text-center hover:shadow-lg transition-all"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-[#882629] rounded-xl mx-auto mb-4">
                  <i className={`${factor.icon} text-3xl text-white`}></i>
                </div>
                <h4 className="text-lg font-semibold text-[#505A5A] mb-3">
                  {factor.title}
                </h4>
                <p className="text-sm text-[#505A5A]/70 leading-relaxed">
                  {factor.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 bg-white rounded-2xl p-8 shadow-md border-l-4 border-[#882629]">
          <p className="text-[#505A5A]/70 italic">
            <strong className="text-[#505A5A] not-italic">Market Note:</strong> Data based on 2025-2026 market analysis. Actual rates and occupancy vary by property type, amenities, and management quality. Professional management typically achieves 15-25% higher occupancy rates.
          </p>
        </div>
      </div>
    </section>
  );
}
